import {Column, Form} from '../../../../stores/Form';
import {priority} from '@priority-software/priority-proto-api/lib';
import {useMemo, useState} from 'react';
import {findDefaultTab, MAX_FIELDS_PER_COLUMN} from '../../../../stores/Form/utils';

export interface IFormViewControl extends priority.netitems.form.IRowViewColumnsDesign {
    Column: Column;
}

export interface IFormViewSection {
    Rows: IFormViewControl[][];
}

export interface IFormViewTab
    extends Omit<priority.netitems.form.IRowViewTabsDesign, 'ColumnsDesign'> {
    Sections: IFormViewSection[];
    Id: string;
}

const splitSections = (sections: IFormViewSection[]): IFormViewSection[] => {
    const [left, right] = [{Rows: []}, {Rows: []}];

    sections.forEach(({Rows}) => {
        const target =
            left.Rows.length + (left.Rows.length > 0 ? 1 : 0) + Rows.length <= MAX_FIELDS_PER_COLUMN
                ? left
                : right;

        if (target.Rows.length) target.Rows.push([]);
        target.Rows.push(...Rows);
    });

    return [left, right];
};

export const useFormView = (form: Form) => {
    const data = form.formDesign.rowViewColumnsDesign;
    const tabs: IFormViewTab[] = useMemo(
        () =>
            data.map((tab, i) => {
                const {ColumnsDesign, ...tabProperties} = tab;
                let merge = false;

                const rawSections = ColumnsDesign.reduce((acc, curr) => {
                    if (curr.Newarea) {
                        acc.push({Rows: []}); // create an empty section
                    }

                    const lastSection = acc[acc.length - 1];

                    if (!curr.Hidden) {
                        if (lastSection) {
                            const control = {
                                ...curr,
                                Column: form.getColumnByName(curr.Name)
                            };

                            if (merge) {
                                // merge the current control with the previous one
                                lastSection.Rows[lastSection.Rows.length - 1].push(control);
                                merge = false;
                            } else {
                                merge = curr.Merge; // check if the current control should be merged with the next one
                                lastSection.Rows.push([control]);
                            }
                        }
                    }
                    return acc;
                }, [] as IFormViewSection[]);

                const needsSplit = rawSections.length > 2;
                const sections = needsSplit ? splitSections(rawSections) : rawSections;

                return {
                    ...tabProperties,
                    Sections: sections,
                    Id: tabProperties.Title
                };
            }),
        [data, form]
    );

    const [activeTab, setActiveTab] = useState(findDefaultTab(tabs, form.currentColumnId)?.Id);

    return {
        tabs,
        activeTab,
        setActiveTab
    };
};
